import { rolesQuery } from "@common/api/roles"
import type { RoleKind, RolePublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { GridTable } from "@common/components/ui/grid-table"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { ShieldCheck } from "lucide-react"
import { z } from "zod"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import {
  SettingsTableCell,
  SettingsTableHead,
  SettingsTableHeader,
  SettingsTableRow,
} from "@/routes/_layout/settings/_panel/-components/settings-table"
import { AUTHORITY_ROLES } from "@/routes/_layout/settings/-lib/roles"

const COLUMNS = "grid-cols-[max-content_minmax(0,1fr)_auto]"

const KIND_LABELS: Record<RoleKind, string> = {
  custom: "Custom",
  default: "Default",
}

const KIND_ORDER: Record<RoleKind, number> = {
  custom: 0,
  default: 1,
}

const filterSchema = z.object({
  search: z.string().optional().catch(""),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/roles/_list",
)({
  validateSearch: (search) => filterSchema.parse(search),
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(rolesQuery()),
  component: RolesPage,
})

function RolesPage(): React.ReactElement {
  const { search = "" } = Route.useSearch()

  return (
    <div className="flex h-full flex-col">
      <FilterBar>
        <Toolbar search={search} />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto [--filter-bar-height:0px]">
          <RoleResults search={search} />
        </div>
        <Outlet />
      </div>
    </div>
  )
}

function Toolbar({ search }: { search: string }): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const canCreate = useHasPermission("roles:create")
  const filter = (next: string) =>
    navigate({
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })

  return (
    <ListToolbar
      actions={
        <>
          <NameFilter search={search} onSearch={filter} />
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/settings/workspace/roles/create">New role</Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function RoleResults({ search }: { search: string }): React.ReactElement {
  const { data } = useSuspenseQuery(rolesQuery())
  const params = useParams({ strict: false })
  const roleId = "roleId" in params ? params.roleId : undefined
  const needle = search.toLowerCase()
  const roles = data.data
    .filter(
      (role) =>
        !role.is_system_role &&
        !AUTHORITY_ROLES.has(role.name) &&
        role.label.toLowerCase().includes(needle),
    )
    .sort(byKindThenName)

  if (roles.length === 0) {
    return <EmptyState search={search} />
  }

  return (
    <GridTable className={COLUMNS}>
      <SettingsTableHeader>
        <SettingsTableHead className="pl-6">Name</SettingsTableHead>
        <SettingsTableHead>Description</SettingsTableHead>
        <SettingsTableHead className="pr-6">Type</SettingsTableHead>
      </SettingsTableHeader>
      {roles.map((role) => (
        <RoleRow key={role.id} role={role} selected={role.id === roleId} />
      ))}
    </GridTable>
  )
}

function RoleRow({
  role,
  selected,
}: {
  role: RolePublic
  selected: boolean
}): React.ReactElement {
  return (
    <SettingsTableRow asChild aria-selected={selected} selected={selected}>
      <Link
        to="/settings/workspace/roles/$roleId"
        params={{ roleId: role.id }}
        search={true}
      >
        <SettingsTableCell className="pl-6">
          <span className="truncate">{role.label}</span>
        </SettingsTableCell>
        <SettingsTableCell className="text-small-regular text-muted-foreground">
          <span className="truncate">{role.description}</span>
        </SettingsTableCell>
        <SettingsTableCell className="pr-6 text-muted-foreground">
          {KIND_LABELS[role.kind]}
        </SettingsTableCell>
      </Link>
    </SettingsTableRow>
  )
}

function byKindThenName(a: RolePublic, b: RolePublic): number {
  return (
    KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.label.localeCompare(b.label)
  )
}

function EmptyState({ search }: { search: string }): React.ReactElement {
  const title = search ? "No matching roles" : "No roles"
  const body = search
    ? "Try a different name."
    : "Create a role to match how your team works."

  return <ListEmpty icon={ShieldCheck} title={title} body={body} />
}
